import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
// Analytics disabled: import { getAnalytics, type Analytics } from 'firebase/analytics';

// Local dev against the Firebase Emulator Suite — no real project/credentials
// needed. Enable with REACT_APP_USE_EMULATORS=true (see .env.local.example).
const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS === 'true';
const EMU_HOST = process.env.REACT_APP_EMULATOR_HOST || 'localhost';

const firebaseConfig = {
  // In emulator mode a demo projectId + any non-empty apiKey is sufficient.
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || (USE_EMULATORS ? 'demo-api-key' : ''),
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || (USE_EMULATORS ? 'localhost' : ''),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || (USE_EMULATORS ? 'demo-ns-gaming' : ''),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
};

// Validate configuration
if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
  console.error('Firebase configuration is incomplete. Please check your environment variables.');
}

// Initialize Firebase app immediately (lightweight)
export const app: FirebaseApp = initializeApp(firebaseConfig);

// Lazy initialization of Firebase services to improve initial load performance
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;
// let _analytics: Analytics | null = null;

// Lazy getters for Firebase services
export const auth = {
  get value(): Auth {
    if (!_auth) {
      _auth = getAuth(app);
      if (USE_EMULATORS) {
        connectAuthEmulator(_auth, `http://${EMU_HOST}:9099`, { disableWarnings: true });
      }
      // S223: forget the sign-in when the tab closes unless this device was chosen to remember it.
      if (!keepSignedIn()) {
        void setPersistence(_auth, browserSessionPersistence).catch(() => undefined);
      }
    }
    return _auth;
  }
};

/*
 * S223 — shared classroom devices.
 *
 * Firebase's default is to remember a sign-in in the browser indefinitely. On the shared
 * Chromebook this product is sold for, a child who closed the tab without pressing Sign Out left
 * the next child inside their account. The default is now the browser SESSION: closing the tab or
 * window signs out. An adult on their own computer can tick "Keep me signed in on this device",
 * which is remembered here (a preference, not a credential) and switches persistence back.
 */
const KEEP_SIGNED_IN_KEY = 'nsgs_keep_signed_in';

export function keepSignedIn(): boolean {
  try {
    return window.localStorage.getItem(KEEP_SIGNED_IN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Call BEFORE signing in: chooses whether this sign-in outlives the browser session. */
export async function setKeepSignedIn(keep: boolean): Promise<void> {
  try {
    if (keep) window.localStorage.setItem(KEEP_SIGNED_IN_KEY, '1');
    else window.localStorage.removeItem(KEEP_SIGNED_IN_KEY);
  } catch {
    // Storage blocked: fall through to the session default, the safer of the two.
  }
  await setPersistence(auth.value, keep ? browserLocalPersistence : browserSessionPersistence);
}

export const db = {
  get value(): Firestore {
    if (!_db) {
      // `ignoreUndefinedProperties` (2026-09-05): without it Firestore THROWS
      // on any document containing an undefined field value, and four services
      // spread optional fields straight into their writes — so creating a
      // classroom, creating an assignment, autosaving a version and recording a
      // skill demonstration all failed in production. The services now strip
      // undefined explicitly (see utils/omitUndefined.ts); this makes the next
      // omission a missing field instead of a dead feature.
      _db = initializeFirestore(app, { ignoreUndefinedProperties: true });
      if (USE_EMULATORS) {
        connectFirestoreEmulator(_db, EMU_HOST, 8080);
      }
    }
    return _db;
  }
};

export const storage = {
  get value(): FirebaseStorage {
    if (!_storage) {
      _storage = getStorage(app);
      if (USE_EMULATORS) {
        connectStorageEmulator(_storage, EMU_HOST, 9199);
      }
    }
    return _storage;
  }
};

/**
 * The ONE place a Functions handle should come from.
 *
 * 2026-09-05: twelve call sites across the app used a bare `getFunctions()`
 * instead. That produces a handle `connectFunctionsEmulator` was never applied
 * to, so every callable — the activity heartbeat, admin user management,
 * teacher invites, screen-time quota changes, the billing portal, the student
 * report, parental consent — aimed at the production cloudfunctions.net URL
 * even when the rest of the app was pointed at the emulator suite. Locally and
 * in E2E they all failed CORS, which is why none of that surface had ever been
 * exercised outside production. Import this, not `getFunctions`.
 */
export const functions = {
  get value(): Functions {
    if (!_functions) {
      _functions = getFunctions(app, 'us-central1'); // Explicitly set region
      if (USE_EMULATORS) {
        connectFunctionsEmulator(_functions, EMU_HOST, 5001);
      }
    }
    return _functions;
  }
};

export const analytics = {
  get value(): null {
    // Disable analytics to prevent network failures in testing
    return null;
  }
};

// Initialize essential services immediately (auth and firestore for core functionality)
export const initializeEssentialServices = (): { auth: Auth; db: Firestore } => {
  return {
    auth: auth.value,
    db: db.value
  };
};

// Initialize all services (call this after initial render to warm lazy singletons)
export const initializeAllServices = (): void => {
  void auth.value;
  void db.value;
  void storage.value;
  void functions.value;
};

// REMOVED 2026-09-05: a module-load `enableNetwork(db.value)`.
//
// The network is enabled by default, so the call did nothing on the happy
// path — but it forced the Firestore client to start during module evaluation,
// before any component had subscribed. When the first watch stream then failed
// (a permission-denied read, which is what a bad project id produces), the SDK
// went into a loop of "INTERNAL ASSERTION FAILED: Unexpected state" throws that
// the app's error handler dutifully reported, two dozen at a time.
//
// Firestore manages its own connection lifecycle; the offline support this
// comment claimed to provide comes from persistence settings, not from this.